import React from "react";
import cn from "classnames";
import { FormGroup, Input, Label } from "reactstrap";
import { useIntl } from "react-intl";
import { FormInput } from "../../../../cores/webForm/interfaces/api/post-public-form";
import {
  formatCategoryFieldValue,
  getCategorySelectPlaceholder,
  getFormInputColumnClass,
  normalizeCategoryOptions,
  parseCategoryFieldValue,
} from "../../../../cores/webForm/utils/formRender.utils";

interface Props {
  input: FormInput;
  invalid?: boolean;
  readOnly?: boolean;
  clean?: boolean;
  columnLayout?: boolean;
  fieldClassName?: string;
  onInputChange?: (input: FormInput, value: string) => void;
}

export default function CategorySelectField({
  input,
  invalid = false,
  readOnly = false,
  clean = false,
  columnLayout = false,
  fieldClassName,
  onInputChange,
}: Props) {
  const intl = useIntl();
  const categories = normalizeCategoryOptions(input.options);
  const categoryPlaceholder = getCategorySelectPlaceholder(
    input.placeholder,
    categories
  );
  const { category, subcategory } = parseCategoryFieldValue(input.value);
  const selectedCategory = categories.find((item) => item.label === category);
  const subcategories = selectedCategory?.subcategories || [];
  const subcategoryPlaceholder = intl.formatMessage({
    id: "app.editForm.subcategories",
  });
  const subcategoryLabel = intl.formatMessage({
    id: "app.editForm.selectSubcategory",
  });

  const wrapSelect = (node: React.ReactNode) =>
    clean ? <div className="web-form-fields__select-wrap">{node}</div> : node;

  const commonProps = {
    disabled: readOnly,
    className: cn(fieldClassName, { "borderRed is-invalid": invalid }),
  };

  const categorySelect = (
    <Input
      {...commonProps}
      type="select"
      name={`field-${input.id}-category`}
      id={`field-${input.id}-category`}
      value={category}
      className={cn(commonProps.className, {
        "web-form-fields__control--empty": clean && !category,
      })}
      onChange={(event) =>
        onInputChange?.(input, formatCategoryFieldValue(event.target.value, ""))
      }
    >
      <option value="">{categoryPlaceholder}</option>
      {categories.map((item) => (
        <option key={item.label} value={item.label}>
          {item.label}
        </option>
      ))}
    </Input>
  );

  const subcategorySelect = (
    <Input
      {...commonProps}
      type="select"
      name={`field-${input.id}-subcategory`}
      id={`field-${input.id}-subcategory`}
      value={subcategory}
      disabled={readOnly || !category}
      className={cn(commonProps.className, {
        "web-form-fields__control--empty": clean && !subcategory,
      })}
      onChange={(event) =>
        onInputChange?.(
          input,
          formatCategoryFieldValue(category, event.target.value)
        )
      }
    >
      <option value="">
        {clean ? subcategoryPlaceholder : subcategoryLabel}
      </option>
      {subcategories.map((item) => (
        <option key={item} value={item}>
          {item}
        </option>
      ))}
    </Input>
  );

  return (
    <FormGroup
      className={cn(
        "w-100 web-form-fields__group",
        columnLayout ? getFormInputColumnClass(input.columnWidth) : undefined,
        {
          "web-form-fields__group--clean": clean,
        }
      )}
    >
      {!clean && (
        <Label
          className="web-form-fields__label"
          for={`field-${input.id}-category`}
        >
          {input.placeholder}
        </Label>
      )}
      {wrapSelect(categorySelect)}

      {category && (
        <>
          {!clean && (
            <Label
              className="web-form-fields__label web-form-fields__subcategory-label"
              for={`field-${input.id}-subcategory`}
            >
              {subcategoryLabel}
            </Label>
          )}
          {clean ? (
            <div className="web-form-fields__select-wrap web-form-fields__subcategory-wrap">
              {subcategorySelect}
            </div>
          ) : (
            subcategorySelect
          )}
        </>
      )}
    </FormGroup>
  );
}
